import { useState, useEffect, memo } from "react";

const WalletConnect = ({
  onAccountSelect,
  selectedAccount,
  showModal,
  setShowModal,
}) => {
  const [availableWallets, setAvailableWallets] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);

  // Wallet configurations for production use
  const SUPPORTED_WALLETS = [
    {
      id: "polkadot-js",
      name: "Polkadot.js Extension",
      description: "Browser extension wallet",
      type: "extension",
      downloadUrl: "https://polkadot.js.org/extension/",
      isPopular: false,
    },
    {
      id: "talisman",
      name: "Talisman",
      description: "Multi-chain browser wallet",
      type: "extension",
      downloadUrl: "https://talisman.xyz/",
      isPopular: true,
    },
    {
      id: "subwallet",
      name: "SubWallet",
      description: "Universal Substrate wallet",
      type: "extension",
      downloadUrl: "https://subwallet.app/",
      isPopular: true,
    },
    {
      id: "nova-wallet",
      name: "Nova Wallet",
      description: "Mobile wallet (scan QR)",
      type: "mobile",
      downloadUrl: "https://novawallet.io/",
      isPopular: true,
    },
    {
      id: "fearless-wallet",
      name: "Fearless Wallet",
      description: "Mobile DeFi wallet",
      type: "mobile",
      downloadUrl: "https://fearlesswallet.io/",
      isPopular: true,
    },
  ];

  useEffect(() => {
    detectWallets();
  }, []);

  const detectWallets = async () => {
    const detected = [];

    try {
      // Check for Polkadot.js extension
      if (window.injectedWeb3 && window.injectedWeb3["polkadot-js"]) {
        detected.push({
          ...SUPPORTED_WALLETS.find((w) => w.id === "polkadot-js"),
          available: true,
        });
      }

      // Check for Talisman
      if (window.injectedWeb3 && window.injectedWeb3["talisman"]) {
        detected.push({
          ...SUPPORTED_WALLETS.find((w) => w.id === "talisman"),
          available: true,
        });
      }

      // Check for SubWallet
      if (window.injectedWeb3 && window.injectedWeb3["subwallet-js"]) {
        detected.push({
          ...SUPPORTED_WALLETS.find((w) => w.id === "subwallet"),
          available: true,
        });
      }

      // Add unavailable wallets for download
      SUPPORTED_WALLETS.forEach((wallet) => {
        if (!detected.find((d) => d.id === wallet.id)) {
          detected.push({
            ...wallet,
            available: false,
          });
        }
      });

      setAvailableWallets(detected);
    } catch (error) {
      console.error("Error detecting wallets:", error);
    }
  };

  const connectWallet = async (walletId) => {
    setConnecting(true);
    setError(null);

    try {
      const { web3Accounts, web3Enable } = await import(
        "@polkadot/extension-dapp"
      );

      const extensions = await web3Enable("REGAChain Explorer");

      if (extensions.length === 0) {
        setError(
          "No wallet extensions found. Please install a Substrate wallet.",
        );
        setConnecting(false);
        return;
      }

      const allAccounts = await web3Accounts();

      if (allAccounts.length === 0) {
        setError("No accounts found. Please create an account in your wallet.");
        setConnecting(false);
        return;
      }

      setAccounts(allAccounts);

      if (allAccounts.length === 1) {
        onAccountSelect(allAccounts[0]);
        setShowModal(false);
      } else {
        setShowModal(false);
        setShowAccountModal(true);
      }
    } catch (error) {
      console.error("Wallet connection error:", error);
      setError(error.message);
    }

    setConnecting(false);
  };

  const selectAccount = (account) => {
    onAccountSelect(account);
    setShowAccountModal(false);
  };

  const WalletModal = () => (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(9, 29, 57, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "480px",
          width: "90%",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h3
            style={{
              fontSize: "20px",
              fontWeight: "600",
              color: "#111827",
              margin: 0,
            }}
          >
            Connect Wallet
          </h3>
          <button
            onClick={() => setShowModal(false)}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              color: "#6b7280",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        <p
          style={{
            color: "#6b7280",
            fontSize: "14px",
            marginBottom: "20px",
          }}
        >
          Choose a wallet to connect to REGAChain Explorer
        </p>

        {error && (
          <div
            style={{
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "6px",
              padding: "12px",
              marginBottom: "16px",
            }}
          >
            <p style={{ color: "#dc2626", fontSize: "14px", margin: 0 }}>
              {error}
            </p>
          </div>
        )}

        <div style={{ display: "grid", gap: "12px" }}>
          {availableWallets.map((wallet) => (
            <div key={wallet.id}>
              <button
                onClick={() =>
                  wallet.available
                    ? connectWallet(wallet.id)
                    : window.open(wallet.downloadUrl, "_blank")
                }
                disabled={connecting}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  padding: "16px",
                  border: wallet.available
                    ? "1px solid #e5e7eb"
                    : "1px solid #fbbf24",
                  borderRadius: "8px",
                  backgroundColor: wallet.available ? "white" : "#fef3c7",
                  cursor: connecting ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  if (!connecting && wallet.available) {
                    e.target.style.backgroundColor = "#f9fafb";
                    e.target.style.borderColor = "#d1d5db";
                  }
                }}
                onMouseLeave={(e) => {
                  if (wallet.available) {
                    e.target.style.backgroundColor = "white";
                    e.target.style.borderColor = "#e5e7eb";
                  } else {
                    e.target.style.backgroundColor = "#fef3c7";
                  }
                }}
              >
                <div
                  style={{
                    fontSize: "24px",
                    marginRight: "12px",
                  }}
                >
                  {wallet.icon}
                </div>
                <div style={{ flex: 1, textAlign: "left" }}>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "500",
                      color: "#111827",
                      marginBottom: "2px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {wallet.name}
                    {wallet.isPopular && (
                      <span
                        style={{
                          backgroundColor: "#10b981",
                          color: "white",
                          fontSize: "10px",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontWeight: "500",
                        }}
                      >
                        POPULAR
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#6b7280",
                    }}
                  >
                    {wallet.description}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: wallet.available ? "#10b981" : "#f59e0b",
                  }}
                >
                  {wallet.available ? "Connect" : "Install"}
                </div>
              </button>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: "20px",
            padding: "16px",
            backgroundColor: "#f0f9ff",
            border: "1px solid #bae6fd",
            borderRadius: "8px",
          }}
        >
          <h4
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: "#0369a1",
              margin: "0 0 8px 0",
            }}
          >
            Mobile Users
          </h4>
          <p
            style={{
              fontSize: "12px",
              color: "#0369a1",
              margin: 0,
            }}
          >
            For mobile wallets like Nova or Fearless, you'll need to use
            WalletConnect or scan QR codes. This feature is coming soon!
          </p>
        </div>
      </div>
    </div>
  );

  const AccountModal = () => (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(9, 29, 57, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "400px",
          width: "90%",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#111827",
              margin: 0,
            }}
          >
            Select Account
          </h3>
          <button
            onClick={() => setShowAccountModal(false)}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              color: "#6b7280",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        <div style={{ display: "grid", gap: "8px" }}>
          {accounts.map((account, index) => (
            <button
              key={index}
              onClick={() => selectAccount(account)}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "12px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                backgroundColor: "white",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f9fafb";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "white";
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#111827",
                  marginBottom: "4px",
                }}
              >
                {account.meta.name}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  fontFamily: "monospace",
                }}
              >
                {account.address}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {!selectedAccount ? (
        <button
          onClick={() => setShowModal(true)}
          style={{
            backgroundColor: "#2563eb",
            color: "white",
            padding: "8px 16px",
            borderRadius: "6px",
            border: "none",
            fontSize: "14px",
            fontWeight: "500",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          Connect Wallet
        </button>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            backgroundColor: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: "6px",
          }}
        >
          <div
            style={{
              width: "8px",
              height: "8px",
              backgroundColor: "#10b981",
              borderRadius: "50%",
            }}
          />
          <div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: "500",
                color: "#166534",
              }}
            >
              {selectedAccount.meta.name}
            </div>
            <div
              style={{
                fontSize: "10px",
                color: "#166534",
                fontFamily: "monospace",
              }}
            >
              {selectedAccount.address.slice(0, 8)}...
              {selectedAccount.address.slice(-8)}
            </div>
          </div>
          <button
            onClick={() => setShowModal(true)}
            style={{
              backgroundColor: "transparent",
              color: "#166534",
              border: "1px solid #bbf7d0",
              borderRadius: "4px",
              padding: "4px 8px",
              fontSize: "10px",
              cursor: "pointer",
            }}
          >
            Change
          </button>
        </div>
      )}

      {showModal && <WalletModal />}
      {showAccountModal && <AccountModal />}
    </>
  );
};

export default memo(WalletConnect);
